import { AdminLayout } from "@/components/admin-layout";
import { HighlightMatches } from "@/components/highlight-matches";
import { getPayoutMemoTexts, PayoutMemoLines } from "@/components/payout-memos";
import { getPayoutSourceLabelLines, getPayoutSourceLabels, getPayoutSourceTypeKeys, getSourceLabel, getSourceTypeKey, payoutRequestSourceTypeOptions, PayoutSourceTags } from "@/components/payout-source";
import { computePayFacetOptions, matchesPayRangeFilters, PayListFilters } from "@/components/pay-list-filters";
import { formatCurrency, getStatusBadgeStyles, payoutStatusFilterOptions } from "@/lib/payout-utils";
import { EarlyDisbursementTag } from "@/components/early-disbursement-tag";
import { OverdueTag } from "@/components/overdue-tag";
import { useOrganizationTimeZone } from "@/hooks/useOrganizationTimeZone";
import { formatRelease } from "@/lib/payout-release";
import {
	DataRefreshEvent,
	listPayables,
	DisbursementTiming,
	PayoutStatus,
	type PayoutListItemDto,
	type PayoutSummaryDto,
} from "@fp/admin-api-client";
import { AdminPageHeader } from "@/components/admin-page-header";
import { formatLocalDate } from "@fp/ui/components/common/local-date-time";
import {
	PageContentContainer,
	PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { PayeeIcon } from "@/components/payee-icon";
import { cx } from "@fp/ui/utils/cx";
import {
	LIST_SHORTCUTS,
	PayoutShortcutsHelp,
} from "@/components/payout-shortcuts-help";
import { type PayoutCategory, PayoutCategoryLabels } from "@/lib/pay-categories";
import { writePayoutsQueue } from "@/lib/payouts-queue";
import { useCallback, useMemo, useState } from "react";
import { useDataRefresh } from "@/hooks/useSignalR";
import { usePayListFilters } from "@/hooks/usePayListFilters";
import { SelectionControl } from "@/components/selection-control";
import { PayNowModal, type PayNowPayoutNotPaid } from "@/components/pay-now-modal";
import { canBePaidNow, getPayoutsNotPaidNow } from "@/lib/pay-now";
import { getErrorMessage } from "@/lib/axios";
import { toast } from "@/lib/toast";
import { Button } from "@fp/ui/components/base/buttons/button";
import { useGetApiAdminFundingBalance } from "@fp/admin-api-client/funding/funding.client";
import { useGetOrganizationContextCapabilities } from "@fp/admin-api-client/organization-context/organization-context.client";
import { usePayPayoutsNow } from "@fp/admin-api-client/payouts/payouts.client";
import { useQueryClient } from "@tanstack/react-query";


const getCategoryFilter = (category: PayoutCategory): { statuses?: PayoutStatus[]; overdue?: boolean; sortBy?: string; sortDescending?: boolean } => {
	switch (category) {
		case "all":
			return {};
		case "overdue":
			return { overdue: true };
		case "scheduled":
			return { statuses: [PayoutStatus.Scheduled], sortBy: "releaseDate", sortDescending: false };
		case "released":
			return { statuses: [PayoutStatus.Released] };
		case "processing":
			return { statuses: [PayoutStatus.Processing] };
		case "paid":
			return { statuses: [PayoutStatus.Paid] };
		case "failed":
			return { statuses: [PayoutStatus.Failed] };
		case "voided":
			return { statuses: [PayoutStatus.Voided] };
		default:
			return {};
	}
};

export const Route = createFileRoute("/payouts/categories/$category")({
	component: RouteComponent,
	loader: async ({ context: { queryClient }, params }) => {
		const category = params.category as PayoutCategory;
		const { statuses, overdue, sortBy, sortDescending } = getCategoryFilter(category);
		const payouts = await queryClient.fetchQuery({
			queryKey: ["payouts", "category", category],
			queryFn: () => listPayables({ page: 1, pageSize: 1000, statuses, overdue, sortBy, sortDescending }),
		});
		const items = payouts?.data?.items ?? [];
		return {
			category,
			items,
		};
	},
	pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
	const { category, items } = Route.useLoaderData();
	const router = useRouter();

	const invalidatePayouts = useCallback(() => { router.invalidate(); }, [router]);
	useDataRefresh(DataRefreshEvent.PayoutRequestApproved, invalidatePayouts);
	useDataRefresh(DataRefreshEvent.PayoutCreated, invalidatePayouts);
	useDataRefresh(DataRefreshEvent.BatchProcessItemCompleted, invalidatePayouts);
	useDataRefresh(DataRefreshEvent.BatchProcessCompleted, invalidatePayouts);
	useDataRefresh(DataRefreshEvent.PayoutRequestPaid, invalidatePayouts);
	useDataRefresh(DataRefreshEvent.PayoutOverdue, invalidatePayouts);
	useDataRefresh(DataRefreshEvent.PayoutTerminallyFailed, invalidatePayouts);
	useDataRefresh(DataRefreshEvent.PayoutRetried, invalidatePayouts);
	useDataRefresh(DataRefreshEvent.PayoutReleased, invalidatePayouts);
	useDataRefresh(DataRefreshEvent.PayoutVoided, invalidatePayouts);

	return (
		<AdminLayout>
			<AdminPageHeader
				title={PayoutCategoryLabels[category as PayoutCategory]}
				parent={{ url: "/payout-requests", label: "Pay" }}
				titleActions={<PayoutShortcutsHelp shortcuts={LIST_SHORTCUTS} />}
			/>
			<PageContentContainer className="p-0">
				<PayoutList
					key={category}
					category={category as PayoutCategory}
					items={items}
				/>
			</PageContentContainer>
		</AdminLayout>
	);
}

type PayoutRowSelection = {
	isSelected: boolean;
	onToggle: () => void;
};

type PayoutListItemComponentProps = {
	item: PayoutListItemDto;
	highlightQuery: string;
	onOpen: () => void;
	selection?: PayoutRowSelection;
};

const PayoutListItemComponent = ({ item, highlightQuery, onOpen, selection }: PayoutListItemComponentProps) => {
	const sourceLines = getPayoutSourceLabelLines(item.source);
	const hasMemo = getPayoutMemoTexts(item.payout).length > 0;
	const truncateWhenNotSearching = highlightQuery.trim() === "" && "truncate";
	const organizationTimeZone = useOrganizationTimeZone();
	const release = organizationTimeZone ? formatRelease(item.payout, organizationTimeZone) : null;
	const created = formatLocalDate(item.payout.createdDate, "dateWithYear");

	return (
		<div className="flex flex-row items-center w-full border-b border-gray-200 bg-white hover:bg-gray-50">
			{selection && (
				<SelectionControl
					isSelectable
					isSelected={selection.isSelected}
					label={`Select ${item.payout.payeeName ?? "Unknown"}`}
					unavailableLabel={`${item.payout.payeeName ?? "Unknown"} can't be paid now`}
					onToggle={selection.onToggle}
					className="pl-4"
				/>
			)}
			<button
				type="button"
				className="flex flex-row items-center justify-between text-left px-4 py-3 w-full min-w-0 cursor-pointer"
				onClick={selection ? selection.onToggle : onOpen}
			>
				<div className="flex flex-col flex-1 min-w-0">
					<div className="flex flex-row items-start justify-between mb-1">
						<div className="flex items-center gap-2 text-base font-semibold text-brand-600 flex-1 min-w-0 pr-4">
							<PayeeIcon payee={item.payout.payee} className="h-4 w-4 shrink-0" />
							<span className={cx(truncateWhenNotSearching)}>
								<HighlightMatches text={item.payout.payeeName ?? "Unknown"} query={highlightQuery} />
							</span>
						</div>
						<div className="text-base font-semibold text-brand-600 whitespace-nowrap">
							<HighlightMatches text={formatCurrency(item.payout.amount, item.payout.currency)} query={highlightQuery} />
						</div>
					</div>

					{(sourceLines.length > 0 || hasMemo) && (
						<div className="mb-1 pr-4">
							{sourceLines.map((line) => (
								<div key={line} className={cx("text-sm text-gray-700", truncateWhenNotSearching)}>
									<HighlightMatches text={line} query={highlightQuery} />
								</div>
							))}
							<PayoutMemoLines payout={item.payout} highlightQuery={highlightQuery} className={cx(truncateWhenNotSearching)} />
						</div>
					)}

					{item.payout.payoutRequestCount > 1 && (
						<div className="mb-1 pr-4 text-sm text-gray-700">
							{item.payout.payoutRequestCount} payout requests
						</div>
					)}

					<div className="flex flex-row items-center justify-between text-sm text-gray-600">
						<div className="flex items-center gap-2">
							<PayoutSourceTags source={item.source} />
							<PayoutStatusBadge payout={item.payout} />
							{item.payout.overdueSince && <OverdueTag />}
							{item.payout.disbursementTiming === DisbursementTiming.Early && <EarlyDisbursementTag />}
						</div>
						{release ? (
							<div className="flex flex-col items-end text-xs">
								<div className="font-medium text-gray-900">Releases {release}</div>
								<div>Created {created}</div>
							</div>
						) : (
							<div className="text-xs">{created}</div>
						)}
					</div>
				</div>
			</button>
		</div>
	);
};

const PayoutStatusBadge = ({ payout }: { payout: PayoutSummaryDto }) => {
	return (
		<span
			className={cx(
				"inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
				getStatusBadgeStyles(payout),
			)}
		>
			{payout.status}
		</span>
	);
};

type PayoutListProps = {
	category: PayoutCategory;
	items: PayoutListItemDto[];
};

const PayoutList = ({ category, items }: PayoutListProps) => {
	const [searchQuery, setSearchQuery] = useState("");
	const {
		selectedSourceTypes,
		selectedStatuses,
		selectedPayeeNames,
		selectedSourceLabels,
		amountMin,
		amountMax,
		submittedFrom,
		submittedTo,
		setSelectedSourceTypes,
		setSelectedStatuses,
		setSelectedPayeeNames,
		setSelectedSourceLabels,
		setAmountMin,
		setAmountMax,
		setSubmittedFrom,
		setSubmittedTo,
		clearFilters,
	} = usePayListFilters("payoutFilters");

	const { sourceTypeOptions, payeeNames, sourceLabels } = useMemo(() => {
		const facetOptions = computePayFacetOptions(
			items.flatMap((item) =>
				(item.source.sources.length > 0 ? item.source.sources : [null]).map((requestSource) => ({
					sourceTypeKey: requestSource ? (getSourceTypeKey(requestSource) ?? null) : null,
					status: category === "all" ? item.payout.status : null,
					payeeName: item.payout.payeeName ?? "Unknown",
					sourceLabel: requestSource ? (getSourceLabel(requestSource) ?? null) : null,
					amount: item.payout.amount,
					createdDate: item.payout.createdDate,
				})),
			),
			{
				sourceTypes: selectedSourceTypes,
				statuses: selectedStatuses,
				payeeNames: selectedPayeeNames,
				sourceLabels: selectedSourceLabels,
				amountMin,
				amountMax,
				submittedFrom,
				submittedTo,
			},
		);

		return {
			sourceTypeOptions: payoutRequestSourceTypeOptions.filter(
				(option) => selectedSourceTypes.has(option.id) || facetOptions.sourceTypeKeys.has(option.id),
			),
			payeeNames: facetOptions.payeeNames,
			sourceLabels: facetOptions.sourceLabels,
		};
	}, [items, category, selectedSourceTypes, selectedStatuses, selectedPayeeNames, selectedSourceLabels, amountMin, amountMax, submittedFrom, submittedTo]);

	const filteredItems = useMemo(() => {
		const query = searchQuery.trim().toLowerCase();
		return items.filter((item) => {
			const { payout, source } = item;
			const payeeName = payout.payeeName ?? "Unknown";
			if (
				selectedSourceTypes.size > 0 &&
				!getPayoutSourceTypeKeys(source).some((key) => selectedSourceTypes.has(key))
			) {
				return false;
			}
			if (
				category === "all" &&
				selectedStatuses.size > 0 &&
				!selectedStatuses.has(payout.status)
			) {
				return false;
			}
			if (selectedPayeeNames.size > 0 && !selectedPayeeNames.has(payeeName)) {
				return false;
			}
			if (
				selectedSourceLabels.size > 0 &&
				!getPayoutSourceLabels(source).some((label) => selectedSourceLabels.has(label))
			) {
				return false;
			}
			if (
				!matchesPayRangeFilters(payout.amount, payout.createdDate, {
					amountMin,
					amountMax,
					submittedFrom,
					submittedTo,
				})
			) {
				return false;
			}
			if (query === "") {
				return true;
			}
			const searchTargets = [
				payeeName,
				...getPayoutSourceLabelLines(source),
				...getPayoutMemoTexts(payout),
				formatCurrency(payout.amount, payout.currency),
				payout.amount.toFixed(2),
			];
			return searchTargets.some((target) => target.toLowerCase().includes(query));
		});
	}, [items, category, searchQuery, selectedSourceTypes, selectedStatuses, selectedPayeeNames, selectedSourceLabels, amountMin, amountMax, submittedFrom, submittedTo]);

	const navigate = useNavigate();

	const payoutQueue = useMemo(
		() => filteredItems.map((item) => item.payout.id),
		[filteredItems],
	);

	const openPayout = useCallback(
		(payoutId: string) => {
			writePayoutsQueue(payoutQueue);
			navigate({
				to: "/payouts/$payoutId",
				params: { payoutId },
				state: {
					returnTo: `/payouts/categories/${category}`,
					returnLabel: PayoutCategoryLabels[category],
				},
			});
		},
		[navigate, category, payoutQueue],
	);

	const router = useRouter();
	const queryClient = useQueryClient();
	const { data: capabilitiesData } = useGetOrganizationContextCapabilities();
	const { data: balanceData } = useGetApiAdminFundingBalance();
	const payPayoutsNowMutation = usePayPayoutsNow();
	const [isSelecting, setIsSelecting] = useState(false);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [isPayNowOpen, setIsPayNowOpen] = useState(false);
	const [payoutsNotPaid, setPayoutsNotPaid] = useState<PayNowPayoutNotPaid[]>([]);
	const canPayNow = category === "scheduled" && (capabilitiesData?.data?.canPayPayoutsNow ?? false);
	const availableBalance = balanceData?.data?.availableBalance ?? 0;

	const payableNowItems = useMemo(() => {
		const now = new Date();
		return filteredItems.filter((item) => canBePaidNow(item.payout, now));
	}, [filteredItems]);
	const payableNowTotal = payableNowItems.reduce((sum, item) => sum + item.payout.amount, 0);
	const selectedItems = useMemo(
		() => payableNowItems.filter((item) => selectedIds.has(item.payout.id)),
		[payableNowItems, selectedIds],
	);
	const selectedTotal = selectedItems.reduce((sum, item) => sum + item.payout.amount, 0);
	const displayedItems = isSelecting ? payableNowItems : filteredItems;

	const enterPayNow = () => {
		setSelectedIds(new Set(payableNowItems.map((item) => item.payout.id)));
		setIsSelecting(true);
	};

	const exitPayNow = () => {
		setIsSelecting(false);
		setSelectedIds(new Set());
	};

	const toggleSelected = (payoutId: string) => {
		setSelectedIds((previous) => {
			const next = new Set(previous);
			if (next.has(payoutId)) {
				next.delete(payoutId);
			} else {
				next.add(payoutId);
			}
			return next;
		});
	};

	const openPayNow = () => {
		setPayoutsNotPaid([]);
		setIsPayNowOpen(true);
	};

	const payNow = async (reason: string) => {
		try {
			await payPayoutsNowMutation.mutateAsync({ data: { payoutIds: selectedItems.map((item) => item.payout.id), reason } });
			setIsPayNowOpen(false);
			exitPayNow();
			await queryClient.invalidateQueries({ queryKey: ["payouts"] });
			await router.invalidate();
			toast.success(selectedItems.length === 1 ? "Payout paid now" : `${selectedItems.length} payouts paid now`);
		} catch (error) {
			const notPaid = getPayoutsNotPaidNow(error);
			if (notPaid === null) {
				toast.error("Couldn't pay these payouts now", { description: getErrorMessage(error, "Failed to pay payouts now") });
				return;
			}
			const payeeNameById = new Map(selectedItems.map((item) => [item.payout.id, item.payout.payeeName ?? "Unknown"]));
			setPayoutsNotPaid(notPaid.map((payout) => ({ ...payout, payeeName: payeeNameById.get(payout.payoutId) ?? "Unknown" })));
			await router.invalidate();
		}
	};

	if (items.length === 0) {
		return (
			<div className="bg-white text-center py-8 text-gray-500">
				No payouts found
			</div>
		);
	}

	return (
		<div className="flex flex-col bg-white">
			<PayListFilters
				searchPlaceholder="Search payouts"
				searchQuery={searchQuery}
				onSearchQueryChange={setSearchQuery}
				sourceTypeOptions={sourceTypeOptions}
				selectedSourceTypes={selectedSourceTypes}
				onSelectedSourceTypesChange={setSelectedSourceTypes}
				statusOptions={payoutStatusFilterOptions}
				showStatusFilters={category === "all"}
				selectedStatuses={selectedStatuses}
				onSelectedStatusesChange={setSelectedStatuses}
				payeeNames={payeeNames}
				selectedPayeeNames={selectedPayeeNames}
				onSelectedPayeeNamesChange={setSelectedPayeeNames}
				sourceLabels={sourceLabels}
				selectedSourceLabels={selectedSourceLabels}
				onSelectedSourceLabelsChange={setSelectedSourceLabels}
				amountMin={amountMin}
				onAmountMinChange={setAmountMin}
				amountMax={amountMax}
				onAmountMaxChange={setAmountMax}
				submittedFrom={submittedFrom}
				onSubmittedFromChange={setSubmittedFrom}
				submittedTo={submittedTo}
				onSubmittedToChange={setSubmittedTo}
				onClearFilters={clearFilters}
			/>
			{canPayNow && !isSelecting && payableNowItems.length > 0 && (
				<div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
					<div className="text-sm text-gray-700">
						{payableNowItems.length} {payableNowItems.length === 1 ? "payout" : "payouts"} can be paid now ·{" "}
						{formatCurrency(payableNowTotal, "USD")}
					</div>
					<Button color="primary" size="sm" onClick={enterPayNow}>
						Pay now
					</Button>
				</div>
			)}
			{isSelecting && (
				<div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3">
					<div className="flex flex-col gap-1 text-sm text-gray-700">
						<span>
							{selectedItems.length} of {payableNowItems.length} selected
						</span>
						<span>
							{formatCurrency(selectedTotal, "USD")} · Available Balance{" "}
							<span className={availableBalance < selectedTotal ? "font-medium text-red-600" : "font-medium text-gray-900"}>
								{formatCurrency(availableBalance, "USD")}
							</span>
						</span>
						<div className="flex items-center gap-2">
							<Button
								color="link-color"
								size="sm"
								isDisabled={selectedItems.length === payableNowItems.length}
								onClick={() => setSelectedIds(new Set(payableNowItems.map((item) => item.payout.id)))}
							>
								Select all
							</Button>
							<span className="text-gray-400">·</span>
							<Button color="link-color" size="sm" isDisabled={selectedItems.length === 0} onClick={() => setSelectedIds(new Set())}>
								Deselect all
							</Button>
						</div>
					</div>
					<div className="flex items-center gap-2">
						<Button color="secondary" size="sm" onClick={exitPayNow}>
							Cancel
						</Button>
						<Button color="primary" size="sm" isDisabled={selectedItems.length === 0} onClick={openPayNow}>
							Pay selected now ({selectedItems.length})
						</Button>
					</div>
				</div>
			)}
			{displayedItems.length === 0 && (
				<div className="text-center py-8 text-gray-500">
					No payouts match the current filters
				</div>
			)}
			{displayedItems.map((item) => (
				<PayoutListItemComponent
					key={item.payout.id}
					item={item}
					highlightQuery={searchQuery}
					onOpen={() => openPayout(item.payout.id)}
					selection={isSelecting ? { isSelected: selectedIds.has(item.payout.id), onToggle: () => toggleSelected(item.payout.id) } : undefined}
				/>
			))}
			<PayNowModal
				isOpen={isPayNowOpen}
				onOpenChange={setIsPayNowOpen}
				payouts={selectedItems.map((item) => item.payout)}
				availableBalance={availableBalance}
				isSubmitting={payPayoutsNowMutation.isPending}
				payoutsNotPaid={payoutsNotPaid}
				onConfirm={payNow}
			/>
		</div>
	);
};